// Analytics Service - API calls for creator analytics features
import { API_URL } from '../config';
import { getAuthHeaders } from './api';

// Feature flag status for a user
export interface FeatureStatus {
  enabled: boolean;
  override: string;
  global_enabled: boolean;
  name: string;
  description: string;
}

// Shoot performance metrics
export interface ShootPerformance {
  shoot_id: number;
  shoot_title: string;
  period_days: number;
  visibility: {
    total_views: number;
    unique_visitors: number;
  };
  requests: {
    total: number;
    pending: number;
    accepted: number;
    confirmed: number;
    request_rate: number;
    acceptance_rate: number;
  };
  quality: {
    avg_score: number | null;
    high_quality_count: number;
    high_quality_percent: number;
  };
  spots: {
    total: number;
    filled: number;
    fill_rate: number;
  };
  trends: {
    views_by_day: Array<{ date: string; count: number }>;
    requests_by_day: Array<{ date: string; count: number }>;
  };
  traffic_sources: Array<{ source: string; count: number }>;
}

// GuestRequest quality analytics
export interface RequestQuality {
  period_days: number;
  total_requests: number;
  quality_breakdown: {
    high: { count: number; percent: number };
    medium: { count: number; percent: number };
    low: { count: number; percent: number };
    unscored: { count: number; percent: number };
  };
  avg_score: number | null;
  score_change: number | null;
  platform_breakdown: Array<{ platform: string; count: number; percent: number }>;
  niche_breakdown: Array<{ niche: string; count: number; percent: number }>;
}

// Repeat guest data
export interface RepeatGuest {
  guest_id: number;
  name: string;
  profile_image: string | null;
  appearance_count: number;
  first_appearance: string | null;
  last_appearance: string | null;
  avg_match_score: number | null;
  is_repeat: boolean;
}

export interface RepeatGuestsData {
  total_unique_guests: number;
  total_appearances: number;
  repeat_guests_count: number;
  repeat_rate: number;
  avg_appearances_per_guest: number;
  top_repeat_guests: RepeatGuest[];
  all_repeat_guests: RepeatGuest[];
}

// Achievement data
export interface Achievement {
  key: string;
  name: string;
  description: string;
  icon: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  category: string;
  earned_at?: string;
  seen?: boolean;
  current?: number;
  target?: number;
  progress_percent?: number;
}

export interface AchievementsData {
  earned_count: number;
  total_achievements: number;
  unseen_count: number;
  earned: Achievement[];
  in_progress: Achievement[];
}

// Spot views data
export interface SpotViewData {
  spot_id: number;
  spot_title: string;
  shoot_id: number;
  shoot_title: string;
  total_views: number;
  unique_visitors: number;
  requests: number;
  conversion_rate: number;
  status: string;
}

export interface SpotViewsResponse {
  period_days: number;
  spots: SpotViewData[];
  summary: {
    total_spots: number;
    total_views: number;
    total_requests: number;
  };
}

// Smart alert
export interface SmartAlert {
  id: number;
  type: 'opportunity' | 'warning' | 'milestone' | 'tip' | 'action_needed';
  title: string;
  message: string;
  priority: 'high' | 'medium' | 'low';
  action_label: string | null;
  action_url: string | null;
  read: boolean;
  created_at: string;
}

export interface AlertsData {
  unread_count: number;
  alerts: SmartAlert[];
}

// Creator overview
export interface CreatorOverview {
  period_days: number;
  shoots: {
    total: number;
    active: number;
  };
  views: {
    total: number;
    change_percent: number | null;
  };
  requests: {
    total: number;
    pending: number;
    change_percent: number | null;
  };
  quality: {
    avg_score: number | null;
  };
  bookings: {
    confirmed: number;
  };
}

// Helper to build headers
const getHeaders = (_userId?: string | number) => ({
  'Content-Type': 'application/json',
  ...getAuthHeaders(),
});

// ==================== FEATURE FLAGS ====================

export async function getMyFeatures(userId: string | number): Promise<Record<string, FeatureStatus>> {
  const response = await fetch(`${API_URL}/api/feature-flags/my-features`, {
    headers: getHeaders(userId),
  });
  if (!response.ok) throw new Error('Failed to fetch feature flags');
  const data = await response.json();
  return data.features;
}

export async function checkFeature(featureKey: string, userId?: string | number): Promise<boolean> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...getAuthHeaders() };

  const response = await fetch(`${API_URL}/api/feature-flags/check/${featureKey}`, { headers });
  if (!response.ok) return false;
  const data = await response.json();
  return data.enabled;
}

// ==================== UTM TRACKING HELPERS ====================

// Get UTM params from current URL
export function getUtmParams(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
} {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  utmKeys.forEach(key => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });
  
  return utm;
}

// Store UTM params in sessionStorage for tracking across pages
export function storeUtmParams(): void {
  const utm = getUtmParams();
  if (Object.keys(utm).length > 0) {
    sessionStorage.setItem('utm_params', JSON.stringify(utm));
  }
}

// Get stored UTM params (falls back to URL params)
export function getStoredUtmParams(): ReturnType<typeof getUtmParams> {
  // First check URL for fresh UTM params
  const urlUtm = getUtmParams();
  if (Object.keys(urlUtm).length > 0) {
    storeUtmParams(); // Store for later pages
    return urlUtm;
  }
  
  // Fall back to stored params
  const stored = sessionStorage.getItem('utm_params');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return parsed;
    } catch {
      return {};
    }
  }
  return {};
}

// ==================== REFERRAL ATTRIBUTION ====================

const REFERRAL_STORAGE_KEY = 'gs_referral_code';
const REFERRAL_LAST_VISIT_KEY = 'gs_referral_last_visit_code';
const REFERRAL_SESSION_VISIT_KEY = 'gs_referral_session_visit_code';
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{6,64}$/;

/**
 * Capture the opaque referral code from the current URL for this browser
 * session. Referral attribution deliberately expires with sessionStorage so
 * stale links do not claim unrelated activity weeks later.
 */
export function getStoredReferralCode(): string | undefined {
  const rawUrlCode = new URLSearchParams(window.location.search).get('gs_ref');
  if (rawUrlCode !== null) {
    const urlCode = rawUrlCode.trim();
    if (REFERRAL_CODE_PATTERN.test(urlCode)) {
      sessionStorage.setItem(REFERRAL_STORAGE_KEY, urlCode);
      return urlCode;
    }
    // An explicit malformed code must not silently fall back to attribution
    // captured from an unrelated earlier page in this browser session.
    sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
    return undefined;
  }

  const stored = sessionStorage.getItem(REFERRAL_STORAGE_KEY)?.trim();
  if (stored && REFERRAL_CODE_PATTERN.test(stored)) return stored;
  if (stored) sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  return undefined;
}

export interface ReferralLinkResponse {
  code: string;
  target: string;
  channel: string;
  campaign?: string | null;
  generation?: number;
  share_url?: string;
}

export async function createReferralLink(
  target: string,
  channel: string,
  campaign?: string,
): Promise<ReferralLinkResponse> {
  const parentCode = getStoredReferralCode();
  const response = await fetch(`${API_URL}/api/growth-lab/referral-links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      target,
      channel,
      ...(campaign ? { campaign } : {}),
      ...(parentCode ? { parent_code: parentCode } : {}),
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || `Unable to create referral link (${response.status})`);
  }
  return response.json();
}

export type ReferralEventType =
  | 'landing'
  | 'explore'
  | 'apply_started'
  | 'return_visit';

export async function trackReferralEvent(
  eventType: ReferralEventType,
  details?: Record<string, any>,
  referralCode?: string,
): Promise<boolean> {
  const code = referralCode || getStoredReferralCode();
  if (!code) return false;

  try {
    const response = await fetch(
      `${API_URL}/api/growth-lab/referral-events/${encodeURIComponent(code)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          event_type: eventType,
          session_id: getSessionId(),
          ...(details ? { details } : {}),
        }),
      },
    );
    if (!response.ok) {
      console.warn('Referral attribution was rejected:', response.status);
      return false;
    }
    return true;
  } catch (error) {
    // Attribution must never block the user workflow.
    console.warn('Referral attribution failed:', error);
    return false;
  }
}

async function trackReferralLanding(
  details: Record<string, any>,
): Promise<void> {
  const code = getStoredReferralCode();
  if (!code) return;
  if (sessionStorage.getItem(REFERRAL_SESSION_VISIT_KEY) === code) return;

  const isReturnVisit = localStorage.getItem(REFERRAL_LAST_VISIT_KEY) === code;
  const recorded = await trackReferralEvent(
    isReturnVisit ? 'return_visit' : 'landing',
    details,
    code,
  );
  if (recorded) {
    sessionStorage.setItem(REFERRAL_SESSION_VISIT_KEY, code);
    localStorage.setItem(REFERRAL_LAST_VISIT_KEY, code);
  }
}

// ==================== VIEW TRACKING ====================

export async function trackShootView(shootId: number, userId?: string | number, sessionId?: string): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...getAuthHeaders() };
  if (sessionId) headers['X-Session-ID'] = sessionId;
  
  // Get UTM params from URL or session storage
  const utmParams = getStoredUtmParams();
  
  const [viewResult] = await Promise.allSettled([
    fetch(`${API_URL}/api/analytics/track/shoot-view/${shootId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(utmParams),
    }),
    // Acquisition attribution is independent from the legacy view endpoint:
    // one telemetry outage must not suppress the other signal.
    trackReferralLanding({ target_type: 'shoot', target_id: shootId }),
  ]);
  if (viewResult.status === 'rejected') {
    console.error('📊 [UTM] Track error:', viewResult.reason);
  } else if (!viewResult.value.ok) {
    console.warn('Shoot view tracking was rejected:', viewResult.value.status);
  }
}

export async function trackSpotView(spotId: number, userId?: string | number, sessionId?: string): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...getAuthHeaders() };
  if (sessionId) headers['X-Session-ID'] = sessionId;
  
  const [viewResult] = await Promise.allSettled([
    fetch(`${API_URL}/api/analytics/track/spot-view/${spotId}`, {
      method: 'POST',
      headers,
    }),
    trackReferralLanding({ target_type: 'spot', target_id: spotId }),
  ]);
  if (viewResult.status === 'rejected') {
    console.warn('Spot view tracking failed:', viewResult.reason);
  } else if (!viewResult.value.ok) {
    console.warn('Spot view tracking was rejected:', viewResult.value.status);
  }
}

export async function trackGuestRequestEvent(
  guestSpotId: number,
  eventType: 'view' | 'apply_clicked' | 'apply_started',
  requestId?: number,
  userId?: string | number,
  sessionId?: string
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...getAuthHeaders() };
  if (sessionId) headers['X-Session-ID'] = sessionId;

  const params = new URLSearchParams({
    guest_spot_id: String(guestSpotId),
    event_type: eventType,
  });
  if (requestId) params.append('request_id', String(requestId));
  
  const response = await fetch(`${API_URL}/api/analytics/track/application-event?${params}`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) {
    throw new Error(`Application event tracking was rejected (${response.status})`);
  }
}

// ==================== SHOOT PERFORMANCE ====================

export async function getShootPerformance(
  shootId: number,
  userId: string | number,
  days: number = 30
): Promise<ShootPerformance> {
  const response = await fetch(
    `${API_URL}/api/analytics/shoots/${shootId}/performance?days=${days}`,
    { headers: getHeaders(userId) }
  );
  if (!response.ok) throw new Error('Failed to fetch shoot performance');
  return response.json();
}

// ==================== SPOT VIEWS ====================

export async function getSpotViews(
  userId: string | number,
  days: number = 30,
  shootId?: number
): Promise<SpotViewsResponse> {
  let url = `${API_URL}/api/analytics/spots/views?days=${days}`;
  if (shootId) url += `&shoot_id=${shootId}`;
  
  const response = await fetch(url, { headers: getHeaders(userId) });
  if (!response.ok) throw new Error('Failed to fetch spot views');
  return response.json();
}

// ==================== REQUEST QUALITY ====================

export async function getRequestQuality(
  userId: string | number,
  days: number = 30,
  shootId?: number
): Promise<RequestQuality> {
  let url = `${API_URL}/api/analytics/request-quality?days=${days}`;
  if (shootId) url += `&shoot_id=${shootId}`;
  
  const response = await fetch(url, { headers: getHeaders(userId) });
  if (!response.ok) throw new Error('Failed to fetch request quality');
  return response.json();
}

// ==================== REPEAT GUESTS ====================

export async function getRepeatGuests(userId: string | number): Promise<RepeatGuestsData> {
  const response = await fetch(`${API_URL}/api/analytics/repeat-guests`, {
    headers: getHeaders(userId),
  });
  if (!response.ok) throw new Error('Failed to fetch repeat guests');
  return response.json();
}

// ==================== ACHIEVEMENTS ====================

export async function getAchievements(userId: string | number): Promise<AchievementsData> {
  const response = await fetch(`${API_URL}/api/analytics/achievements`, {
    headers: getHeaders(userId),
  });
  if (!response.ok) throw new Error('Failed to fetch achievements');
  return response.json();
}

export async function markAchievementSeen(
  achievementKey: string,
  userId: string | number
): Promise<void> {
  const response = await fetch(
    `${API_URL}/api/analytics/achievements/${achievementKey}/mark-seen`,
    {
      method: 'POST',
      headers: getHeaders(userId),
    }
  );
  if (!response.ok) throw new Error('Failed to mark achievement seen');
}

// ==================== SMART ALERTS ====================

export async function getAlerts(
  userId: string | number,
  unreadOnly: boolean = false,
  limit: number = 20
): Promise<AlertsData> {
  const response = await fetch(
    `${API_URL}/api/analytics/alerts?unread_only=${unreadOnly}&limit=${limit}`,
    { headers: getHeaders(userId) }
  );
  if (!response.ok) throw new Error('Failed to fetch alerts');
  return response.json();
}

export async function markAlertRead(alertId: number, userId: string | number): Promise<void> {
  const response = await fetch(`${API_URL}/api/analytics/alerts/${alertId}/read`, {
    method: 'POST',
    headers: getHeaders(userId),
  });
  if (!response.ok) throw new Error('Failed to mark alert read');
}

export async function dismissAlert(alertId: number, userId: string | number): Promise<void> {
  const response = await fetch(`${API_URL}/api/analytics/alerts/${alertId}/dismiss`, {
    method: 'POST',
    headers: getHeaders(userId),
  });
  if (!response.ok) throw new Error('Failed to dismiss alert');
}

export async function markAllAlertsRead(userId: string | number): Promise<void> {
  const response = await fetch(`${API_URL}/api/analytics/alerts/mark-all-read`, {
    method: 'POST',
    headers: getHeaders(userId),
  });
  if (!response.ok) throw new Error('Failed to mark all alerts read');
}

// ==================== CREATOR OVERVIEW ====================

export async function getCreatorOverview(
  userId: string | number,
  days: number = 30
): Promise<CreatorOverview> {
  const response = await fetch(`${API_URL}/api/analytics/overview?days=${days}`, {
    headers: getHeaders(userId),
  });
  if (!response.ok) throw new Error('Failed to fetch creator overview');
  return response.json();
}

// ==================== CAMPAIGN ANALYTICS ====================

export interface CampaignAnalytics {
  period_days: number;
  total_views: number;
  tracked_views: number;
  untracked_views: number;
  by_source: Array<{ source: string; count: number }>;
  by_medium: Array<{ medium: string; count: number }>;
  by_campaign: Array<{ campaign: string; count: number }>;
  top_campaigns: Array<{
    source: string;
    medium: string | null;
    campaign: string | null;
    count: number;
  }>;
}

export async function getCampaignAnalytics(
  userId: string | number,
  days: number = 30,
  shootId?: number
): Promise<CampaignAnalytics> {
  let url = `${API_URL}/api/analytics/campaigns?days=${days}`;
  if (shootId) url += `&shoot_id=${shootId}`;
  
  const response = await fetch(url, { headers: getHeaders(userId) });
  if (!response.ok) throw new Error('Failed to fetch campaign analytics');
  return response.json();
}

// ==================== BEHAVIORAL TRACKING ====================

// Session ID management - persists across page loads but resets on browser close
let _sessionId: string | null = null;

export function getSessionId(): string {
  if (!_sessionId) {
    // Try to get from sessionStorage first
    _sessionId = sessionStorage.getItem('gs_session_id');
    if (!_sessionId) {
      // Generate new session ID
      _sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      sessionStorage.setItem('gs_session_id', _sessionId);
    }
  }
  return _sessionId;
}

export interface BehaviorEvent {
  event_type: string;
  target_type?: string;
  target_id?: number;
  event_data?: Record<string, any>;
  page_url?: string;
  referrer?: string;
}

export async function trackBehavior(
  event: BehaviorEvent,
  userId?: string | number
): Promise<{ status: string; event_id?: number }> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    headers['X-Session-ID'] = getSessionId();

    const response = await fetch(`${API_URL}/api/analytics/track/behavior`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...event,
        page_url: event.page_url || window.location.href,
        referrer: event.referrer || document.referrer,
      }),
    });
    
    if (!response.ok) {
      console.warn('Failed to track behavior event:', response.status);
      return { status: 'error' };
    }
    
    return response.json();
  } catch (error) {
    console.warn('Error tracking behavior:', error);
    return { status: 'error' };
  }
}

export async function trackProfileView(
  viewedUserId: number,
  source?: string,
  userId?: string | number
): Promise<void> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    headers['X-Session-ID'] = getSessionId();

    const url = source
      ? `${API_URL}/api/analytics/track/profile-view/${viewedUserId}?source=${encodeURIComponent(source)}`
      : `${API_URL}/api/analytics/track/profile-view/${viewedUserId}`;
    
    await fetch(url, { method: 'POST', headers });
  } catch (error) {
    console.warn('Error tracking profile view:', error);
  }
}

export interface SearchTrackingData {
  query?: string;
  filters?: Record<string, any>;
  sort_by?: string;
  results_count?: number;
  results_shown?: number;
}

export async function trackSearch(
  data: SearchTrackingData,
  userId?: string | number
): Promise<number | null> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    headers['X-Session-ID'] = getSessionId();

    const response = await fetch(`${API_URL}/api/analytics/track/search`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) return null;
    
    const result = await response.json();
    return result.search_id || null;
  } catch (error) {
    console.warn('Error tracking search:', error);
    return null;
  }
}

export async function trackSearchClick(
  searchId: number,
  resultId: number,
  position: number
): Promise<void> {
  try {
    const headers: Record<string, string> = { ...getAuthHeaders() };
    headers['X-Session-ID'] = getSessionId();

    await fetch(
      `${API_URL}/api/analytics/track/search/${searchId}/click?result_id=${resultId}&position=${position}`,
      { method: 'PATCH', headers }
    );
  } catch (error) {
    console.warn('Error tracking search click:', error);
  }
}

// Convenience functions for common events
export const trackPageView = (page: string, userId?: string | number) =>
  trackBehavior({ event_type: 'page_view', event_data: { page } }, userId);

export const trackClick = (element: string, targetType?: string, targetId?: number, userId?: string | number) =>
  trackBehavior({ event_type: 'click', target_type: targetType, target_id: targetId, event_data: { element } }, userId);

export const trackScroll = (depth: number, page: string, userId?: string | number) =>
  trackBehavior({ event_type: 'scroll', event_data: { depth, page } }, userId);

export const trackTimeOnPage = (seconds: number, page: string, userId?: string | number) =>
  trackBehavior({ event_type: 'time_on_page', event_data: { seconds, page } }, userId);

export const trackFilterChange = (filter: string, oldValue: any, newValue: any, userId?: string | number) =>
  trackBehavior({ event_type: 'filter_change', event_data: { filter, old_value: oldValue, new_value: newValue } }, userId);

export const trackRequestStart = (spotId: number, userId?: string | number) =>
  trackBehavior({ event_type: 'request_start', target_type: 'spot', target_id: spotId }, userId);

export const trackRequestAbandon = (spotId: number, userId?: string | number) =>
  trackBehavior({ event_type: 'request_abandon', target_type: 'spot', target_id: spotId }, userId);

export const trackShare = (contentType: string, contentId: number, platform?: string, userId?: string | number) =>
  trackBehavior({ event_type: 'share', target_type: contentType, target_id: contentId, event_data: { platform } }, userId);

// ==================== ADMIN ANALYTICS ====================

export interface PlatformOverview {
  period_days: number;
  users: {
    total: number;
    new: number;
    active: number;
  };
  content: {
    total_shoots: number;
    new_shoots: number;
    total_spots: number;
  };
  requests: {
    total: number;
    new: number;
    confirmed: number;
    conversion_rate: number;
  };
  engagement: {
    total_events: number;
    authenticated_events?: number | null;
    total_searches: number;
    total_profile_views: number;
    events_per_user: number;
  };
  event_breakdown: Array<{ event: string; count: number }>;
  daily_active_users: Array<{ date: string; users: number }>;
  top_searches: Array<{ query: string; count: number }>;
}

export async function getPlatformOverview(
  userId: string | number,
  days: number = 30
): Promise<PlatformOverview> {
  const response = await fetch(
    `${API_URL}/api/analytics/admin/platform-overview?days=${days}`,
    { headers: getHeaders(userId) }
  );
  if (!response.ok) throw new Error('Failed to fetch platform overview');
  return response.json();
}

export interface UserJourneys {
  period_days: number;
  total_sessions: number;
  avg_events_per_session: number;
  common_paths: Array<{ path: string; count: number }>;
  sample_journeys: Array<{
    session_id: string;
    user_id: number | null;
    event_count: number;
    duration_seconds: number;
    events: Array<{ type: string; target: string | null; page: string | null }>;
  }>;
}

export async function getUserJourneys(
  userId: string | number,
  days: number = 7,
  limit: number = 100
): Promise<UserJourneys> {
  const response = await fetch(
    `${API_URL}/api/analytics/admin/user-journeys?days=${days}&limit=${limit}`,
    { headers: getHeaders(userId) }
  );
  if (!response.ok) throw new Error('Failed to fetch user journeys');
  return response.json();
}

export interface SearchInsights {
  period_days: number;
  total_searches: number;
  successful_searches: number;
  success_rate: number;
  zero_result_searches: number;
  zero_result_rate: number;
  top_queries: Array<{
    query: string;
    count: number;
    avg_results: number;
    click_rate: number;
  }>;
  top_filters: Array<{ filter: string; count: number }>;
}

export async function getSearchInsights(
  userId: string | number,
  days: number = 30
): Promise<SearchInsights> {
  const response = await fetch(
    `${API_URL}/api/analytics/admin/search-insights?days=${days}`,
    { headers: getHeaders(userId) }
  );
  if (!response.ok) throw new Error('Failed to fetch search insights');
  return response.json();
}

// ==================== INVESTOR METRICS (admin-only) ====================

export type InvestorPeriod = '7d' | '30d' | '90d' | 'all';

export interface InvestorMetricsHero {
  active_users_30d: number;
  total_bookings_completed: number;
  median_creator_response_hours: number | null;
  creator_response_rate_pct: number;
  creator_response_sample_size: number;
  match_success_rate_pct: number;
  weekly_growth_pct: number | null;
  weekly_growth_metric: 'signups' | 'bookings';
}

export interface InvestorMetricsGrowthRow {
  week_start: string;
  new_signups: number;
  applications_submitted: number;
  acceptances: number;
  collabs_completed: number;
  messages_sent: number;
}

export interface InvestorMetricsFunnelRow {
  key: string;
  label: string;
  count: number;
  pct_of_signups: number;
}

export interface InvestorMetricsMarketplaceHealth {
  spots_posted_period: number;
  median_apps_per_spot: number;
  pct_spots_with_zero_apps_after_7d: number;
  median_hours_to_first_application: number | null;
  median_hours_to_creator_response: number | null;
  creator_response_rate_pct: number;
  creator_response_sample_size: number;
  spots_filled_count: number;
  open_spot_count: number;
}

export interface InvestorMetricsOutcomes {
  total_collaboration_outcomes: number;
  avg_preparation_rating: number | null;
  avg_punctuality_pct_on_time: number | null;
  avg_communication_rating: number | null;
  avg_promotion_pct_shared: number | null;
  pct_creator_would_collab_again: number | null;
  pct_guest_would_collab_again: number | null;
  pct_with_content_url: number | null;
  median_follower_delta_per_collab: number | null;
  audience_snapshot_count: number;
  dismissal_count: number;
  empty_state: boolean;
}

export interface InvestorMetricsEngagement {
  median_sessions_per_user_per_week: number;
  median_actions_per_session: number;
  median_thread_length_per_booking: number;
  profile_completion_distribution: Array<{ bucket: string; count: number }>;
}

export interface InvestorMetricsNetwork {
  total_unique_creator_guest_pairs: number;
  repeat_guest_pct: number;
  multi_creator_guest_pct: number;
  total_guests_with_bookings: number;
}

export interface InvestorMetricsSyntheticSplit {
  bot_actions_24h: number;
  real_user_events_24h: number;
  bot_bookings_30d: number;
  real_bookings_30d: number;
  pct_synthetic_bookings_30d: number;
  narrative: string;
}

export interface InvestorMetricsPlatformHealth {
  bug_feed_24h_total: number;
  bug_feed_severity_breakdown: { http_5xx: number; http_4xx: number; exception: number };
  failed_bot_runs_24h: number;
  bot_monitor_link: string;
}

export interface InvestorMetrics {
  meta: {
    period: InvestorPeriod;
    period_days: number;
    include_bots: boolean;
    generated_at: string;
    cache_ttl_seconds: number;
  };
  hero: InvestorMetricsHero;
  growth_time_series: InvestorMetricsGrowthRow[];
  funnel: InvestorMetricsFunnelRow[];
  marketplace_health: InvestorMetricsMarketplaceHealth;
  outcomes_data_moat: InvestorMetricsOutcomes;
  engagement_depth: InvestorMetricsEngagement;
  network_density: InvestorMetricsNetwork;
  synthetic_vs_real_split: InvestorMetricsSyntheticSplit;
  platform_health: InvestorMetricsPlatformHealth;
}

export async function getInvestorMetrics(
  userId: string | number,
  period: InvestorPeriod = '30d',
  includeBots: boolean = false,
): Promise<InvestorMetrics> {
  const params = new URLSearchParams({
    period,
    include_bots: String(includeBots),
  });
  const response = await fetch(
    `${API_URL}/api/admin/metrics/investor?${params.toString()}`,
    { headers: getHeaders(userId) },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Failed to fetch investor metrics (${response.status}): ${text}`);
  }
  return response.json();
}

// ==================== CREATOR BEHAVIOR ANALYTICS ====================

export interface ProfileViewsData {
  period_days: number;
  total_views: number;
  unique_viewers: number;
  views_by_source: Array<{ source: string; count: number }>;
  views_by_day: Array<{ date: string; count: number }>;
  recent_viewers: Array<{
    user_id: number;
    username: string;
    display_name: string | null;
    profile_image: string | null;
    viewed_at: string;
    source: string | null;
  }>;
}

export async function getProfileViews(
  userId: string | number,
  days: number = 30
): Promise<ProfileViewsData> {
  const response = await fetch(
    `${API_URL}/api/analytics/creator/profile-views?days=${days}`,
    { headers: getHeaders(userId) }
  );
  if (!response.ok) throw new Error('Failed to fetch profile views');
  return response.json();
}

export interface EngagementFunnel {
  period_days: number;
  funnel: Array<{ stage: string; count: number }>;
  conversion_rates: {
    profile_to_shoot: number;
    shoot_to_spot: number;
    spot_to_request: number;
    request_to_book: number;
    overall: number;
  };
}

export async function getEngagementFunnel(
  userId: string | number,
  days: number = 30
): Promise<EngagementFunnel> {
  const response = await fetch(
    `${API_URL}/api/analytics/creator/engagement-funnel?days=${days}`,
    { headers: getHeaders(userId) }
  );
  if (!response.ok) throw new Error('Failed to fetch engagement funnel');
  return response.json();
}


// ==================== STICKINESS / ENGAGEMENT FEATURES ====================

export type RequestEngagementType = 'inbox_impression' | 'pitch_open';

/** Record a truthful, server-deduplicated creator engagement event. */
export async function trackRequestEngagement(
  requestId: number,
  userId: string | number,
  eventType: RequestEngagementType
): Promise<{ status: string; event_type?: RequestEngagementType; occurred_at?: string }> {
  try {
    const response = await fetch(
      `${API_URL}/api/analytics/track/request-engagement/${requestId}`,
      {
        method: 'POST',
        headers: {
          ...getHeaders(userId),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ event_type: eventType }),
      }
    );
    if (!response.ok) return { status: 'error' };
    return response.json();
  } catch (error) {
    console.error('Failed to track request engagement:', error);
    return { status: 'error' };
  }
}

/**
 * @deprecated Legacy page-load compatibility only. This deliberately creates
 * no impression or pitch-open signal; new callers must name observed evidence.
 */
export async function trackRequestView(
  requestId: number,
  userId: string | number
) {
  try {
    const response = await fetch(
      `${API_URL}/api/analytics/track/application-view/${requestId}`,
      {
        method: 'POST',
        headers: getHeaders(userId),
      }
    );
    if (!response.ok) return { status: 'error' };
    return response.json();
  } catch (error) {
    console.error('Legacy request-view call failed:', error);
    return { status: 'error' };
  }
}

/**
 * Record a user visit for streak tracking
 */
export async function recordUserVisit(
  userId: string | number
): Promise<{ visit_streak?: number; longest_streak?: number; total_visits?: number }> {
  try {
    const response = await fetch(
      `${API_URL}/api/analytics/engagement/record-visit`,
      {
        method: 'POST',
        headers: getHeaders(userId),
      }
    );
    if (!response.ok) return {};
    return response.json();
  } catch (error) {
    console.error('Failed to record visit:', error);
    return {};
  }
}

/**
 * Get user engagement stats (streaks, achievements, etc.)
 */
export interface EngagementStats {
  visit_streak: number;
  longest_streak: number;
  total_visits: number;
  profile_completeness: number;
  level: string;
  achievements: string[];
  last_visit?: string;
}

export async function getEngagementStats(
  userId: string | number
): Promise<EngagementStats> {
  const response = await fetch(
    `${API_URL}/api/analytics/engagement/stats`,
    { headers: getHeaders(userId) }
  );
  if (!response.ok) {
    return {
      visit_streak: 0,
      longest_streak: 0,
      total_visits: 0,
      profile_completeness: 0,
      level: 'newbie',
      achievements: [],
    };
  }
  return response.json();
}
